// Plunger (§3.3): a projectile at 18 m/s along the aim (or facing), radius 0.3, flying at the
// user's height + 0.6 m for `lifeTicks`. It is stopped by solids (taller than its height), and
// sticks to the first body it sweeps into (not its user, |Δz| ≤ 1.2): both then enter YANKED
// toward each other (yank.ts). Fired while FALLING (z > −1.5) it is a plunger save: the user rises
// toward 0.2 m during the yank and lands if the yank brought it back over ground.
//
// Predict mode (§18.2): no projectile is spawned; one already in the world only flies.
import { DT, NO_SLOT, TUNE } from '../constants';
import { EV, emit } from '../events';
import { BS, Cause, EK } from '../ids';
import { MAPS } from '../maps/index';
import { SolidKind, type Solid } from '../maps/mapTypes';
import { MODES } from '../modes/index';
import { clampLength, distToSegmentSq, segmentCircleHit } from '../math';
import type { Bean, Entity, World } from '../types';
import { popFloat } from './balloon';
import { freeFromCarrier, releaseCarry } from './carry';
import { addDizzy, applyKnockback, canBeHit } from './combat';
import { dcos, dsin } from './dmath';
import { removeEntity, spawnEntity } from './entities';
import { STATE_TABLE } from './stateTable';
import { isPredicting, isTeammate, setTouch } from './world';
import { breakYank, startYank } from './yank';

const solids: Solid[] = [];
/** Sample points along one tick of flight for capsule solids (a 0.3 m step is under any solid's reach). */
const CAPSULE_SAMPLES = [0.5, 1] as const;

/** Item use: fires a plunger from the bean's front along the aim (or facing). */
export function firePlunger(w: World, b: Bean, ax: number, ay: number): void {
  if (isPredicting(w)) return;
  const p = TUNE.plunger;
  const l = Math.hypot(ax, ay);
  const ux = l > 0 ? ax / l : dcos(b.facing);
  const uy = l > 0 ? ay / l : dsin(b.facing);
  const e = spawnEntity(w, EK.PLUNGER, b.slot, b.pos.x + ux * b.radius, b.pos.y + uy * b.radius, b.z + p.z);
  e.vel.x = ux * p.speed;
  e.vel.y = uy * p.speed;
  e.timer = p.lifeTicks;
  e.data = NO_SLOT;
  e.data2 = b.state === BS.FALLING ? 1 : 0;
}

/** States a plunger user can be yanked from (it has a free body, or it is falling: the save). */
function yankableUser(u: Bean): boolean {
  if (!u.active || u.out) return false;
  switch (u.state) {
    case BS.NORMAL: case BS.LUNGE: case BS.TUMBLE: case BS.SLIP: case BS.GRAB_START: case BS.GRAB_MISS:
    case BS.PUNCH_WINDUP: case BS.PUNCH: case BS.FALLING:
      return true;
    default:
      return false;
  }
}

/** Earliest fraction of this tick's flight at which the plunger meets a solid, or 2 for none. */
function solidHitT(w: World, e: Entity, x1: number, y1: number): number {
  const n = MAPS[w.cfg.mapId].solids(w, solids);
  const r = TUNE.plunger.radius;
  let best = 2;
  for (let i = 0; i < n; i++) {
    const s = solids[i];
    if (e.z >= s.top - TUNE.collide.topSlack) continue;
    if (s.kind === SolidKind.CIRCLE) {
      const t = segmentCircleHit(e.pos.x, e.pos.y, x1, y1, s.ax, s.ay, s.r + r);
      if (t >= 0 && t < best) best = t;
      continue;
    }
    const reach2 = (s.r + r) * (s.r + r);
    for (const t of CAPSULE_SAMPLES) {
      if (t >= best) break;
      const px = e.pos.x + (x1 - e.pos.x) * t;
      const py = e.pos.y + (y1 - e.pos.y) * t;
      if (distToSegmentSq(px, py, s.ax, s.ay, s.bx, s.by) <= reach2) {
        best = t;
        break;
      }
    }
  }
  return best;
}

/**
 * Plunger connects with `v` (§3.3): +8 Dizzy and credit (none for a teammate), then both yank.
 * Teetering, hanging and climbing victims fall instead (§2.14: any hit), carried victims are freed,
 * carriers drop, floating victims pop. A user that can no longer be yanked wastes the plunger.
 */
function plungerHit(w: World, e: Entity, v: Bean): void {
  const p = TUNE.plunger;
  const u = w.beans[e.owner];
  if (!u || !yankableUser(u)) return;
  if (STATE_TABLE[v.state].hit === 'falls') {
    applyKnockback(w, v.slot, v.pos.x - u.pos.x, v.pos.y - u.pos.y, 0, { dizzy: p.dizzy, cause: Cause.PLUNGER, by: u.slot, scaleByDizzy: false, noTumble: true, teammateCheck: true });
    emit(w, EV.PLUNGER_HIT, u.slot, v.slot, 0, 0, v.pos.x, v.pos.y);
    return;
  }
  switch (v.state) {
    case BS.CARRYING: releaseCarry(w, v, 'hit'); break;
    case BS.GRABBED: freeFromCarrier(w, v); break;
    case BS.FLOAT: popFloat(w, v, u.slot); break;
    case BS.YANKED: breakYank(v); break;
    case BS.LUNGE: clampLength(v.vel, v.vel.x, v.vel.y, TUNE.bean.runSpeed); break;
    default: break;
  }
  if (!isTeammate(w, u.slot, v.slot)) {
    addDizzy(w, v, p.dizzy);
    setTouch(w, v.slot, u.slot, Cause.PLUNGER);
  }
  v.lastHitTick = w.tick;
  const save = u.state === BS.FALLING;
  startYank(w, u, v, save);
  emit(w, EV.PLUNGER_HIT, u.slot, v.slot, save ? 1 : 0, 0, v.pos.x, v.pos.y);
  MODES[w.cfg.modeId].onHit(w, u.slot, v.slot, Cause.PLUNGER);
}

/** Entity phase for a PLUNGER: swept flight against bodies and solids, then the hit or the end of its range. */
export function stepPlunger(w: World, e: Entity): void {
  const x1 = e.pos.x + e.vel.x * DT;
  const y1 = e.pos.y + e.vel.y * DT;
  if (isPredicting(w)) {
    e.pos.x = x1;
    e.pos.y = y1;
    return;
  }
  const p = TUNE.plunger;
  let target: Bean | null = null;
  let bestT = 2;
  for (const b of w.beans) {
    if (b.slot === e.owner || !canBeHit(b) || Math.abs(b.z - e.z) > p.maxDz) continue;
    const t = segmentCircleHit(e.pos.x, e.pos.y, x1, y1, b.pos.x, b.pos.y, b.radius + p.radius);
    if (t >= 0 && t < bestT) {
      bestT = t;
      target = b;
    }
  }
  const wallT = solidHitT(w, e, x1, y1);
  if (wallT < bestT) {
    removeEntity(w, e.id);
    return;
  }
  if (target) {
    removeEntity(w, e.id);
    plungerHit(w, e, target);
    return;
  }
  e.pos.x = x1;
  e.pos.y = y1;
  if (--e.timer <= 0) removeEntity(w, e.id);
}
